import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';

const InsideWorkflowDetails = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [workflowStatus, setWorkflowStatus] = useState('active');

  const workflowStats = {
    totalRuns: 1247,
    successRate: 98.4,
    avgExecutionTime: '2.3s',
    lastRun: '5 minutes ago'
  };

  const recentExecutions = [
    {
      id: 'exec_001',
      status: 'success',
      startTime: '2024-01-15 14:30:22',
      duration: '2.1s',
      trigger: 'Webhook',
      steps: 5
    },
    {
      id: 'exec_002',
      status: 'success',
      startTime: '2024-01-15 14:28:15',
      duration: '1.9s',
      trigger: 'Schedule',
      steps: 5
    },
    {
      id: 'exec_003',
      status: 'failed',
      startTime: '2024-01-15 14:25:08',
      duration: '0.8s',
      trigger: 'Webhook',
      steps: 3,
      error: 'API timeout'
    },
    {
      id: 'exec_004',
      status: 'success',
      startTime: '2024-01-15 14:22:45',
      duration: '2.4s',
      trigger: 'Webhook',
      steps: 5
    }
  ];

  const workflowSteps = [
    {
      id: 1,
      type: 'trigger',
      name: 'New User Registration',
      description: 'Webhook receives new user data',
      status: 'active'
    },
    {
      id: 2,
      type: 'condition',
      name: 'Check User Type',
      description: 'Determine if user is premium or standard',
      status: 'active'
    },
    {
      id: 3,
      type: 'action',
      name: 'Send Welcome Email',
      description: 'Personalized welcome based on user type',
      status: 'active'
    },
    {
      id: 4,
      type: 'action',
      name: 'Create User Profile',
      description: 'Initialize user in CRM system',
      status: 'active'
    },
    {
      id: 5,
      type: 'action',
      name: 'Notify Sales Team',
      description: 'Alert sales for premium users',
      status: 'active'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-500 bg-green-500/10';
      case 'failed': return 'text-red-500 bg-red-500/10';
      case 'running': return 'text-blue-500 bg-blue-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getStepIcon = (type) => {
    switch (type) {
      case 'trigger': return '⚡';
      case 'condition': return '🔀';
      case 'action': return '⚙️';
      default: return '📋';
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 mb-6">
          <Link className="text-gray-400 hover:text-primary transition-colors text-sm font-medium" to="/workflow-builder">Workflows</Link>
          <span className="text-gray-500 font-medium">/</span>
          <span className="text-white text-sm font-bold">Customer Onboarding Flow</span>
        </nav>

        {/* Workflow Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
              <span className="text-3xl">🔄</span>
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-white">Customer Onboarding Flow</h1>
              <p className="text-gray-400 mt-1">Automated user registration and welcome sequence</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
              workflowStatus === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'
            }`}>
              {workflowStatus}
            </div>
            <button 
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                workflowStatus === 'active' 
                  ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20' 
                  : 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
              }`}
              onClick={() => setWorkflowStatus(workflowStatus === 'active' ? 'paused' : 'active')}
            >
              {workflowStatus === 'active' ? 'Pause' : 'Activate'}
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-8">
          <button className="flex items-center gap-2 rounded-lg h-10 px-4 bg-gray-800 text-white text-sm font-bold hover:bg-gray-700 transition-colors border border-gray-700">
            <span className="text-sm">📤</span>
            <span>Export</span>
          </button>
          <button className="flex items-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors">
            <span className="text-sm">✏️</span>
            <span>Edit Workflow</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm font-medium">Total Runs</span>
              <span className="text-2xl">🚀</span>
            </div>
            <p className="text-2xl font-bold text-white">{workflowStats.totalRuns.toLocaleString()}</p>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm font-medium">Success Rate</span>
              <span className="text-2xl">✅</span>
            </div>
            <p className="text-2xl font-bold text-green-500">{workflowStats.successRate}%</p>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm font-medium">Avg Execution</span>
              <span className="text-2xl">⏱️</span>
            </div>
            <p className="text-2xl font-bold text-white">{workflowStats.avgExecutionTime}</p>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm font-medium">Last Run</span>
              <span className="text-2xl">🕐</span>
            </div>
            <p className="text-2xl font-bold text-primary">{workflowStats.lastRun}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700 mb-8">
          <nav className="flex gap-8">
            {['overview', 'executions', 'settings'].map((tab) => (
              <button
                key={tab}
                className={`pb-4 text-sm font-bold capitalize transition-colors ${
                  activeTab === tab
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-400 hover:text-white'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Workflow Steps */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-white">
                <span className="text-xl">📋</span>
                Workflow Steps
              </h3>
              <div className="space-y-4">
                {workflowSteps.map((step, index) => (
                  <div key={step.id} className="flex items-center gap-4 p-4 rounded-lg border border-gray-700 hover:border-primary/50 transition-colors">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-xl">{getStepIcon(step.type)}</span>
                      <div>
                        <p className="font-bold text-sm text-white">{step.name}</p>
                        <p className="text-xs text-gray-400">{step.description}</p>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(step.status)}`}>
                      {step.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-white">
                <span className="text-xl">📊</span>
                Recent Executions
              </h3>
              <div className="space-y-3">
                {recentExecutions.map((execution) => (
                  <div key={execution.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-700 hover:bg-gray-700 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        execution.status === 'success' ? 'bg-green-500' : 
                        execution.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                      }`}></div>
                      <div>
                        <p className="text-sm font-bold text-white">{execution.id}</p>
                        <p className="text-xs text-gray-400">{execution.startTime}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-white">{execution.duration}</p>
                      <p className="text-xs text-gray-400">{execution.steps} steps</p>
                      {execution.error && (
                        <p className="text-xs text-red-500">{execution.error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 py-2 text-sm font-bold text-primary hover:bg-primary/5 rounded-lg transition-colors">
                View All Executions
              </button>
            </div>
          </div>
        )}

        {activeTab === 'executions' && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">Execution History</h3>
              <p className="text-sm text-gray-400 mt-1">Complete log of workflow executions</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Execution ID</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Start Time</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Trigger</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Steps</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {recentExecutions.map((execution) => (
                    <tr key={execution.id} className="hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">{execution.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(execution.status)}`}>
                          {execution.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{execution.startTime}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">{execution.duration}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{execution.trigger}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{execution.steps}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-6 text-white">Workflow Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-2 text-white">Workflow Name</label>
                  <input 
                    className="w-full rounded-lg border border-gray-700 bg-gray-700 text-white p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    defaultValue="Customer Onboarding Flow"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2 text-white">Description</label>
                  <textarea 
                    className="w-full rounded-lg border border-gray-700 bg-gray-700 text-white p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    rows="3"
                    defaultValue="Automated user registration and welcome sequence"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2 text-white">Execution Timeout</label>
                  <select className="w-full rounded-lg border border-gray-700 bg-gray-700 text-white p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none">
                    <option>30 seconds</option>
                    <option>1 minute</option>
                    <option>5 minutes</option>
                    <option>10 minutes</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-6 text-white">Notifications</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm text-white">Execution Failures</p>
                    <p className="text-xs text-gray-400">Get notified when workflow fails</p>
                  </div>
                  <div className="w-10 h-5 bg-primary rounded-full flex items-center justify-end px-1 cursor-pointer">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm text-white">Daily Summary</p>
                    <p className="text-xs text-gray-400">Daily execution report</p>
                  </div>
                  <div className="w-10 h-5 bg-gray-600 rounded-full flex items-center justify-start px-1 cursor-pointer">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm text-white">Performance Alerts</p>
                    <p className="text-xs text-gray-400">Alert on slow executions</p>
                  </div>
                  <div className="w-10 h-5 bg-primary rounded-full flex items-center justify-end px-1 cursor-pointer">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default InsideWorkflowDetails;